import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../utils/auth';
import { maintenanceAPI, sensorAPI } from '../utils/api';
import { MaintenanceSchedule, SensorData } from '../types';
import { 
  Wrench, 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle,
  Plus,
  RefreshCw,
  Settings,
  FileText,
  Home
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MaintenanceDashboard: React.FC = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [generators, setGenerators] = useState<SensorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'overdue' | 'due_soon' | 'upcoming'>('all');
  const [selectedGenerator, setSelectedGenerator] = useState<number | null>(null);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');

  useEffect(() => {
    fetchData();
  }, [filter, selectedGenerator, serviceTypeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch schedules - always filter by active status
      const params: any = { status: 'active' };
      if (filter === 'overdue') params.is_overdue = true;
      if (selectedGenerator) params.generator_id = selectedGenerator;
      
      const schedulesResponse = await maintenanceAPI.getSchedules(params);
      let filteredSchedules = schedulesResponse.schedules;

      // Filter by service type if not 'all'
      if (serviceTypeFilter !== 'all') {
        filteredSchedules = filteredSchedules.filter(
          schedule => schedule.template?.service_type === serviceTypeFilter
        );
      }

      setSchedules(filteredSchedules);

      // Fetch generators - always filter by engine_hours type (generators only)
      const sensorsResponse = await sensorAPI.getAllSensors();
      const generatorSensors = sensorsResponse.sensors.filter(s => s.type === 'engine_hours');
      setGenerators(generatorSensors);

    } catch (error: any) {
      console.error('Error fetching maintenance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (schedule: MaintenanceSchedule) => {
    if (schedule.is_overdue) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Overdue
        </span>
      );
    } else if (schedule.is_due_soon) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          Due Soon
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          Upcoming
        </span>
      );
    }
  };

  const formatNextService = (schedule: MaintenanceSchedule) => {
    const parts: string[] = [];
    
    if (schedule.next_service_date) {
      const days = schedule.days_until_next_service ?? 0;
      if (days < 0) {
        parts.push(`${Math.abs(days)} days overdue`);
      } else if (days === 0) {
        parts.push('Today');
      } else {
        parts.push(`In ${days} days`);
      }
    }
    
    if (schedule.next_service_run_hours) {
      const hours = schedule.hours_until_next_service ?? 0;
      if (hours <= 0) {
        parts.push('Hours exceeded');
      } else {
        const hoursNum = typeof hours === 'number' ? hours : parseFloat(String(hours)) || 0;
        parts.push(`${hoursNum.toFixed(1)} hours remaining`);
      }
    }
    
    return parts.join(' • ') || 'Not scheduled';
  };

  const stats = {
    total: schedules.length,
    overdue: schedules.filter(s => s.is_overdue).length,
    dueSoon: schedules.filter(s => s.is_due_soon && !s.is_overdue).length,
    upcoming: schedules.filter(s => !s.is_due_soon && !s.is_overdue).length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Wrench className="w-6 h-6" />
                Maintenance Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">Manage generator maintenance schedules and records</p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Link>
              <Link
                to="/maintenance/templates"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Templates
              </Link>
              <Link
                to="/maintenance/reports"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Reports
              </Link>
              <Link
                to="/maintenance/schedules/new"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Schedule
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Schedules</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Due Soon</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.dueSoon}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-bold text-green-600">{stats.upcoming}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('overdue')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'overdue' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Overdue
              </button>
              <button
                onClick={() => setFilter('due_soon')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'due_soon' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Due Soon
              </button>
              <button
                onClick={() => setFilter('upcoming')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filter === 'upcoming' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Upcoming
              </button>
            </div>

            <select
              value={selectedGenerator || ''}
              onChange={(e) => setSelectedGenerator(e.target.value ? parseInt(e.target.value) : null)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Generators</option>
              {generators.map((gen) => (
                <option key={gen.id} value={gen.id}>
                  {gen.name}
                  {gen.device_info?.name && ` (${gen.device_info.name})`}
                  {gen.location ? ` - ${gen.location}` : ''}
                </option>
              ))}
            </select>

            <select
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Service Types</option>
              <option value="routine">Routine</option>
              <option value="major">Major</option>
              <option value="emergency">Emergency</option>
              <option value="other">Other</option>
            </select>

            <button
              onClick={fetchData}
              className="ml-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Schedules List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading schedules...</p>
          </div>
        ) : schedules.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Wrench className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No maintenance schedules</h3>
            <p className="text-gray-600 mb-4">Create a maintenance schedule to get started.</p>
            <Link
              to="/maintenance/schedules/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Schedule
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => (
              <motion.div
                key={schedule.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {schedule.template?.name || 'Unknown Template'}
                      </h3>
                      {getStatusBadge(schedule)}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Generator</p>
                        <p className="font-medium text-gray-900">
                          {schedule.generator?.name || 'Unknown'}
                          {schedule.generator?.device_name && (
                            <span className="text-gray-600 ml-2">({schedule.generator.device_name})</span>
                          )}
                          {schedule.generator?.location && (
                            <span className="text-gray-500 ml-2">- {schedule.generator.location}</span>
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600 mb-1">Next Service</p>
                        <p className="font-medium text-gray-900">{formatNextService(schedule)}</p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600 mb-1">Current Run Hours</p>
                        <p className="font-medium text-gray-900">
                          {(typeof schedule.current_run_hours === 'number' 
                            ? schedule.current_run_hours 
                            : parseFloat(String(schedule.current_run_hours)) || 0).toFixed(1)} hours
                        </p>
                      </div>

                      {schedule.latest_record && (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Last Service</p>
                          <p className="font-medium text-gray-900">
                            {new Date(schedule.latest_record.service_date).toLocaleDateString()}
                            {schedule.latest_record.service_run_hours && (
                              <span className="text-gray-500 ml-2">
                                ({typeof schedule.latest_record.service_run_hours === 'number' 
                                  ? schedule.latest_record.service_run_hours.toFixed(1) 
                                  : parseFloat(String(schedule.latest_record.service_run_hours)) || 0}h)
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Link
                      to={`/maintenance/schedules/${schedule.id}`}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      View Details
                    </Link>
                    <Link
                      to={`/maintenance/records/new?schedule_id=${schedule.id}`}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Record Service
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceDashboard;

